import React from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * Change triage-manager mode of an EXISTING tenant.
 *
 * `currentModeIsClient` flips the client-mode password placeholder from
 * "initial password" to "new password (only if changing)" — so operators know
 * the field is optional when the tenant already has a local triage user.
 */
export const TriageModeDialog = ({
  open, onOpenChange, tenantName, form, setForm,
  error, saving, currentModeIsClient, onSubmit,
}) => {
  const clientValid = form.triage_mode !== 'client' ||
    (form.triage_name && form.triage_email && form.triage_password.length >= 8);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-slate-50 border-slate-200 text-slate-900" data-testid="triage-mode-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-900">Gestor de Triagem — {tenantName}</DialogTitle>
          <DialogDescription className="text-slate-500">
            Utilizador que recebe todas as notificações do fluxo (novas denúncias, prazos, mudanças de estado).
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="bg-red-50 border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${form.triage_mode === 'cogniseg' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-white'}`}
            data-testid="edit-triage-mode-cogniseg-option"
          >
            <input
              type="radio" name="edit_triage_mode"
              checked={form.triage_mode === 'cogniseg'}
              onChange={() => setForm(prev => ({ ...prev, triage_mode: 'cogniseg' }))}
              className="mt-1"
            />
            <div className="text-sm">
              <div className="font-medium text-slate-900">Serviço CogniSeg</div>
              <div className="text-slate-500">Atribui o utilizador global <code className="text-xs">gestortriagem@cogniseg.com</code>.</div>
            </div>
          </label>
          <label
            className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition ${form.triage_mode === 'client' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-white'}`}
            data-testid="edit-triage-mode-client-option"
          >
            <input
              type="radio" name="edit_triage_mode"
              checked={form.triage_mode === 'client'}
              onChange={() => setForm(prev => ({ ...prev, triage_mode: 'client' }))}
              className="mt-1"
            />
            <div className="text-sm">
              <div className="font-medium text-slate-900">Gestor do próprio cliente</div>
              <div className="text-slate-500">Utilizador local com o role Triage Manager.</div>
            </div>
          </label>
        </div>

        {form.triage_mode === 'client' && (
          <div className="space-y-3">
            <Input
              value={form.triage_name}
              onChange={(e) => setForm(prev => ({ ...prev, triage_name: e.target.value }))}
              placeholder="Nome do Gestor de Triagem"
              className="bg-white border-slate-300 text-slate-900"
              data-testid="edit-triage-name-input"
            />
            <Input
              type="email"
              value={form.triage_email}
              onChange={(e) => setForm(prev => ({ ...prev, triage_email: e.target.value }))}
              placeholder="gestor.triagem@empresa.com"
              className="bg-white border-slate-300 text-slate-900"
              data-testid="edit-triage-email-input"
            />
            <Input
              type="password"
              value={form.triage_password}
              onChange={(e) => setForm(prev => ({ ...prev, triage_password: e.target.value }))}
              placeholder={currentModeIsClient ? 'Nova password (deixar só se quiser alterar)' : 'Password inicial (mín. 8 caracteres)'}
              className="bg-white border-slate-300 text-slate-900"
              data-testid="edit-triage-password-input"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 text-slate-600 hover:bg-slate-100">
            Cancelar
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving || !clientValid}
            className="bg-indigo-700 hover:bg-amber-600 text-black"
            data-testid="save-triage-mode-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
